import { Box, Card, Chip, Divider, Grid, Stack, Typography, Button } from "@mui/material";
import { Link } from "react-router-dom";
import { usePageMeta } from "../hooks/usePageMeta";

export function AboutPage() {
    usePageMeta({
        title: 'About Jeff May',
        description: 'Jeff May is a software architect and AI developer with 25+ years of experience, including NASA Space Shuttle systems. Now helping small businesses automate and grow.',
    });

    const credentials = [
        { label: 'NASA — Space Shuttle Operations Engineer', years: '13 years' },
        { label: 'VA SmartHome — IoT System Architect', years: '3 years' },
        { label: 'Logistics Integration Architect', years: '10 years' },
        { label: 'Independent Software Development', years: '25+ years' },
    ];

    const techStack = [
        'TypeScript', 'React', 'Node.js', '.NET', 'Azure',
        'AI / LLM Integration', 'REST APIs', 'System Integration',
        'IoT', 'Automated Document Generation',
    ];

    return (
        <>
            <Box sx={{ mb: 4 }}>
                <Typography variant='h3' sx={{ fontWeight: 700, mb: 1 }}>About Jeff May</Typography>
                <Typography variant='h6' color='text.secondary' sx={{ fontWeight: 400 }}>
                    Software Architect — AI Developer — Problem Solver
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ mb: 6 }}>
                {/* Photo + quick links */}
                <Grid item xs={12} md={3}>
                    <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                        <Box
                            component='img'
                            src='/images/jeffMay.jpg'
                            alt='Jeff May'
                            sx={{
                                width: '100%',
                                maxWidth: 240,
                                height: 'auto',
                                borderRadius: 2,
                                border: '1px solid #e0e0e0',
                                display: 'block',
                                mb: 2,
                                mx: { xs: 'auto', md: 0 },
                            }}
                        />
                        <Stack spacing={1}>
                            <Button component={Link} to='/portfolio' variant='outlined' size='small' fullWidth>
                                View Portfolio
                            </Button>
                            <Button component={Link} to='/contact' variant='contained' size='small' fullWidth>
                                Get In Touch
                            </Button>
                        </Stack>
                    </Box>
                </Grid>

                {/* Story */}
                <Grid item xs={12} md={9}>
                    <Typography variant='body1' sx={{ mb: 2, lineHeight: 1.8 }}>
                        I've spent 25+ years building software systems across some demanding environments —
                        including 13 years as a propulsion operations engineer on the Space Shuttle program at NASA,
                        where the cost of a bad system design wasn't measured in dollars but in mission risk.
                        That background shaped how I think: understand the problem completely before touching the keyboard,
                        build systems that are reliable under pressure, and make tools that real people actually want to use.
                    </Typography>
                    <Typography variant='body1' sx={{ mb: 2, lineHeight: 1.8 }}>
                        After NASA, I built IoT-based smart home systems for the VA — giving veterans with disabilities
                        more independence at home. Then a decade leading ERP and logistics integrations, connecting
                        supply chains and automating the handoffs that otherwise required someone manually moving data
                        between systems all day.
                    </Typography>
                    <Typography variant='body1' sx={{ mb: 3, lineHeight: 1.8 }}>
                        Today I focus on what I do best: identifying the manual, repetitive, error-prone work inside
                        small businesses and replacing it with AI-assisted automation and practical software. No bloated
                        enterprise contracts. No 6-month timelines. Just focused solutions that work.
                    </Typography>

                    <Divider sx={{ mb: 3 }} />

                    {/* Credentials timeline */}
                    <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>Experience</Typography>
                    <Stack spacing={1.5} sx={{ mb: 3 }}>
                        {credentials.map((c) => (
                            <Box key={c.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                                <Typography variant='body2'>{c.label}</Typography>
                                <Chip label={c.years} size='small' variant='outlined' sx={{ flexShrink: 0 }} />
                            </Box>
                        ))}
                    </Stack>

                    <Divider sx={{ mb: 3 }} />

                    {/* Tech */}
                    <Typography variant='h6' sx={{ fontWeight: 600, mb: 2 }}>Technical Stack</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {techStack.map((t) => (
                            <Chip key={t} label={t} size='small' color='primary' variant='outlined' />
                        ))}
                    </Box>
                </Grid>
            </Grid>

            {/* NASA callout */}
            <Card sx={{ backgroundColor: '#1565c0', color: 'white', p: { xs: 3, md: 4 }, borderRadius: 2, mb: 4 }}>
                <Typography variant='h6' sx={{ fontWeight: 700, mb: 1 }}>
                    🚀 NASA Award Recipient — twice
                </Typography>
                <Typography variant='body2' sx={{ color: 'rgba(255,255,255,0.9)', lineHeight: 1.7 }}>
                    Received NASA performance awards for building mission-critical data tools used by Space Shuttle
                    propulsion engineers during launch operations. If a system had to work, and had to be easy
                    to use under pressure — that's the standard I've always built to.
                </Typography>
            </Card>
        </>
    );
};
